import { memo } from 'react';
import { Avatar, Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { initialsFromName } from '../../utils/initials.js';

const AVATAR_CLASS =
  'border-transparent bg-neutral-800 text-[11px] font-semibold text-white dark:bg-neutral-700';

const QUESTION_STATUS_VARIANTS = {
  awaiting_response: 'warning',
  answered: 'success',
  forwarded: 'informative',
  declined: 'error',
};

// Memoized: rendered inside `comments.map()` with an inline onClick arrow, so without
// this every thread re-render re-renders each question row.
const ThreadQuestionComment = memo(function ThreadQuestionComment({
  comment,
  onMarkAnswered,
  t,
}) {
  const initials = comment.initials || initialsFromName(comment.author);
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-950/20 px-3 py-3 dark:bg-amber-500/5">
      <div className="flex items-start gap-3">
        <Avatar size="md" initials={initials} alt={comment.author} className={AVATAR_CLASS} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium text-content">{comment.author}</span>
            <Badge variant="warning" size="small">
              {t('question.threadBadge')}
            </Badge>
            {comment.questionRecipient ? (
              <span className="text-xs text-content-faint">→ {comment.questionRecipient}</span>
            ) : null}
            <span className="ml-auto text-xs text-content-faint">{comment.timeAgo}</span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-content-secondary">{comment.body}</p>
          {comment.questionStatus ? (
            <div className="mt-3 flex items-center justify-between gap-2">
              <Badge
                variant={QUESTION_STATUS_VARIANTS[comment.questionStatus] ?? 'neutral'}
                size="small"
              >
                {t(`question.status.${comment.questionStatus}`)}
              </Badge>
              {comment.questionStatus === 'awaiting_response' && comment.questionEventId ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onMarkAnswered?.(comment.questionEventId)}
                >
                  {t('question.markAnswered')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

function ThreadMessageComment({ comment }) {
  const initials = comment.initials || initialsFromName(comment.author);
  return (
    <div className="flex gap-3">
      <Avatar size="lg" initials={initials} alt={comment.author} className={AVATAR_CLASS} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-content">{comment.author}</span>
          {comment.role ? <span className="text-xs text-content-faint">{comment.role}</span> : null}
          <span className="ml-auto text-xs text-content-faint">{comment.timeAgo}</span>
        </div>
        <p className="mt-1 text-sm leading-relaxed text-content-secondary">
          {Array.isArray(comment.mentions) && comment.mentions.length > 0
            ? comment.mentions.map((name) => (
                <Badge key={name} variant="informative" size="small" className="mr-1.5 align-middle">
                  @{name}
                </Badge>
              ))
            : null}
          {comment.body}
        </p>
      </div>
    </div>
  );
}

function ThreadCommentList({ comments = [], onMarkAnswered }) {
  const { t } = useTranslation('work-queues-app');

  if (comments.length === 0) {
    return <p className="text-sm text-content-muted">{t('thread.empty')}</p>;
  }

  return (
    <ul className="flex flex-col gap-4">
      {comments.map((comment) => (
        <li key={comment.id}>
          {comment.kind === 'question' ? (
            <ThreadQuestionComment comment={comment} onMarkAnswered={onMarkAnswered} t={t} />
          ) : (
            <ThreadMessageComment comment={comment} />
          )}
        </li>
      ))}
    </ul>
  );
}

export default ThreadCommentList;
